/** A file to put in an archive: its path inside, with `/` between folders, and its bytes. */
export interface IZipEntry {
    path: string;
    data: Uint8Array;
}

const LOCAL_HEADER: number = 0x04034b50;
const CENTRAL_HEADER: number = 0x02014b50;
const END_OF_DIRECTORY: number = 0x06054b50;
/** Version 2.0: enough for stored files in folders. */
const ZIP_VERSION: number = 20;
/** General purpose flag bit 11: names are UTF-8, so Cyrillic names open right on any system. */
const UTF8_NAMES: number = 0x0800;
const LOCAL_HEADER_SIZE: number = 30;
const CENTRAL_HEADER_SIZE: number = 46;
const END_SIZE: number = 22;

const BYTE: number = 256;
const ZIP_POLYNOMIAL: number = 0xedb88320;
const ALL_ONES: number = 0xffffffff;

/** Exclusive or of two bytes, bit by bit in arithmetic: the tree lints bitwise operators away. */
const BYTE_XOR: Uint8Array = Uint8Array.from({ length: BYTE * BYTE }, (_unused: unknown, pair: number): number => {
    let a: number = Math.floor(pair / BYTE);
    let b: number = pair % BYTE;
    let result: number = 0;
    for (let weight: number = 1; weight < BYTE; weight *= 2) {
        result += (a % 2 === b % 2 ? 0 : 1) * weight;
        a = Math.floor(a / 2);
        b = Math.floor(b / 2);
    }
    return result;
});

/** Exclusive or of two unsigned 32-bit numbers, a byte at a time. */
function xor32(a: number, b: number): number {
    let result: number = 0;
    let weight: number = 1;
    for (let index: number = 0; index < 4; index++) {
        result += BYTE_XOR[(Math.floor(a / weight) % BYTE) * BYTE + (Math.floor(b / weight) % BYTE)] * weight;
        weight *= BYTE;
    }
    return result;
}

/** The CRC-32 table of the zip polynomial, one entry per byte value. */
const CRC_TABLE: Uint32Array = Uint32Array.from({ length: BYTE }, (_unused: unknown, byte: number): number => {
    let crc: number = byte;
    for (let bit: number = 0; bit < 8; bit++) {
        // Shift right by one; where the low bit was set, fold in the polynomial.
        crc = crc % 2 === 1 ? xor32(Math.floor(crc / 2), ZIP_POLYNOMIAL) : Math.floor(crc / 2);
    }
    return crc;
});

/** CRC-32 of the bytes, as the zip format checks it. */
export function crc32(data: Uint8Array): number {
    let crc: number = ALL_ONES;
    for (const byte of data) {
        crc = xor32(CRC_TABLE[BYTE_XOR[(crc % BYTE) * BYTE + byte]], Math.floor(crc / BYTE));
    }
    return xor32(crc, ALL_ONES);
}

/** The MS-DOS time and date fields of a moment, local clock, two-second steps, from 1980. */
function dosDateTime(moment: Date): { time: number; date: number } {
    const year: number = Math.max(1980, moment.getFullYear());
    return {
        time: moment.getHours() * 2048 + moment.getMinutes() * 32 + Math.floor(moment.getSeconds() / 2),
        date: (year - 1980) * 512 + (moment.getMonth() + 1) * 32 + moment.getDate(),
    };
}

interface IStoredEntry {
    name: Uint8Array;
    data: Uint8Array;
    crc: number;
    offset: number;
}

function writeLocal(view: DataView, at: number, entry: IStoredEntry, stamp: { time: number; date: number }): void {
    view.setUint32(at, LOCAL_HEADER, true);
    view.setUint16(at + 4, ZIP_VERSION, true);
    view.setUint16(at + 6, UTF8_NAMES, true);
    view.setUint16(at + 8, 0, true);
    view.setUint16(at + 10, stamp.time, true);
    view.setUint16(at + 12, stamp.date, true);
    view.setUint32(at + 14, entry.crc, true);
    view.setUint32(at + 18, entry.data.length, true);
    view.setUint32(at + 22, entry.data.length, true);
    view.setUint16(at + 26, entry.name.length, true);
    view.setUint16(at + 28, 0, true);
}

function writeCentral(view: DataView, at: number, entry: IStoredEntry, stamp: { time: number; date: number }): void {
    view.setUint32(at, CENTRAL_HEADER, true);
    view.setUint16(at + 4, ZIP_VERSION, true);
    view.setUint16(at + 6, ZIP_VERSION, true);
    view.setUint16(at + 8, UTF8_NAMES, true);
    view.setUint16(at + 10, 0, true);
    view.setUint16(at + 12, stamp.time, true);
    view.setUint16(at + 14, stamp.date, true);
    view.setUint32(at + 16, entry.crc, true);
    view.setUint32(at + 20, entry.data.length, true);
    view.setUint32(at + 24, entry.data.length, true);
    view.setUint16(at + 28, entry.name.length, true);
    // Extra field, comment, disk number, internal and external attributes: all zero.
    view.setUint32(at + 42, entry.offset, true);
}

/**
 * A zip archive of the files, stored without compression: local headers with the data, the
 * central directory and its end record. Every entry carries `moment` as its time.
 */
export function zipStore(entries: readonly IZipEntry[], moment: Date): Uint8Array<ArrayBuffer> {
    const encoder: TextEncoder = new TextEncoder();
    const stamp: { time: number; date: number } = dosDateTime(moment);
    let offset: number = 0;
    const stored: IStoredEntry[] = entries.map((entry: IZipEntry): IStoredEntry => {
        const name: Uint8Array = encoder.encode(entry.path);
        const item: IStoredEntry = { name, offset, data: entry.data, crc: crc32(entry.data) };
        offset += LOCAL_HEADER_SIZE + name.length + entry.data.length;
        return item;
    });
    const directoryStart: number = offset;
    const directorySize: number = stored.reduce(
        (sum: number, entry: IStoredEntry): number => sum + CENTRAL_HEADER_SIZE + entry.name.length,
        0
    );
    const bytes: Uint8Array<ArrayBuffer> = new Uint8Array(directoryStart + directorySize + END_SIZE);
    const view: DataView = new DataView(bytes.buffer);
    stored.forEach((entry: IStoredEntry): void => {
        writeLocal(view, entry.offset, entry, stamp);
        bytes.set(entry.name, entry.offset + LOCAL_HEADER_SIZE);
        bytes.set(entry.data, entry.offset + LOCAL_HEADER_SIZE + entry.name.length);
    });
    let at: number = directoryStart;
    stored.forEach((entry: IStoredEntry): void => {
        writeCentral(view, at, entry, stamp);
        bytes.set(entry.name, at + CENTRAL_HEADER_SIZE);
        at += CENTRAL_HEADER_SIZE + entry.name.length;
    });
    view.setUint32(at, END_OF_DIRECTORY, true);
    view.setUint16(at + 8, stored.length, true);
    view.setUint16(at + 10, stored.length, true);
    view.setUint32(at + 12, directorySize, true);
    view.setUint32(at + 16, directoryStart, true);
    return bytes;
}
